import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Send, Paperclip, X, File, Image as ImageIcon, Video, Download, Upload } from 'lucide-react';
import { uploadFileToFirebase } from '../utils/firebaseUpload';
import { formatFileSize } from '../utils/fileHelpers';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Helper to get auth headers
const getAuthHeaders = () => {
  const stored = sessionStorage.getItem('assistant');
  if (stored) {
    try {
      const assistant = JSON.parse(stored);
      return { 'x-assistant-session': assistant.id };
    } catch (e) {}
  }
  return {};
};

const MessagingPage = () => {
  const navigate = useNavigate();
  const { threadId } = useParams();
  const [threads, setThreads] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState('');
  const [newSubject, setNewSubject] = useState('');
  const [showNewMessage, setShowNewMessage] = useState(false);
  const [executives, setExecutives] = useState([]);
  const [selectedExecutive, setSelectedExecutive] = useState('');
  const [attachments, setAttachments] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);
  const [showThreadList, setShowThreadList] = useState(true);
  const fileInputRef = useRef(null);
  const messagesEndRef = useRef(null);
  const pendingFilesRef = useRef({}); // Store actual File objects by ID

  useEffect(() => {
    fetchThreads();
    fetchExecutives();
    
    // Handle window resize for mobile detection
    const handleResize = () => {
      const mobile = window.innerWidth <= 768;
      setIsMobile(mobile);
      if (!mobile) {
        setShowThreadList(true); // Always show thread list on desktop
      }
    };
    
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    if (threadId && threads.length > 0) {
      const thread = threads.find(t => t.thread_id === threadId);
      if (thread) {
        handleSelectThread(thread);
      }
    }
  }, [threadId, threads]);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const fetchThreads = async () => {
    try {
      setLoading(true);
      
      // Fetch regular threads
      const res = await fetch(`${BACKEND_URL}/api/assistant-messages/threads`, {
        headers: getAuthHeaders()
      });
      
      if (res.status === 401) {
        navigate('/assistant-login');
        return;
      }
      
      let regularThreads = [];
      if (res.ok) {
        const data = await res.json();
        regularThreads = data.threads || [];
      }
      
      // Fetch assistant ID from session to get HR/Payroll thread
      const assistantRes = await fetch(`${BACKEND_URL}/api/assistant/profile`, {
        headers: getAuthHeaders()
      });
      
      if (assistantRes.ok) {
        const assistantData = await assistantRes.json();
        const assistantId = assistantData.assistant?.id;
        
        if (assistantId) {
          // Fetch HR/Payroll messages
          const hrRes = await fetch(`${BACKEND_URL}/api/messages/hr-payroll/${assistantId}`, {
            headers: getAuthHeaders()
          });
          
          if (hrRes.ok) {
            const hrData = await hrRes.json();
            if (hrData.messages && hrData.messages.length > 0) {
              // Create HR/Payroll thread entry
              const hrThread = {
                thread_id: `hr_payroll_${assistantId}`,
                thread_type: 'hr_payroll',
                executive_name: 'Human Resources - Payroll',
                executive_id: 'hr_payroll',
                last_message: hrData.messages[hrData.messages.length - 1]?.content?.substring(0, 100) || '',
                last_message_at: hrData.messages[hrData.messages.length - 1]?.created_at,
                unread_count: hrData.messages.filter(m => !m.is_read && m.sender_id !== assistantId).length
              };
              
              // Add HR thread at the top
              setThreads([hrThread, ...regularThreads]);
            } else {
              setThreads(regularThreads);
            }
          } else {
            setThreads(regularThreads);
          }
        } else {
          setThreads(regularThreads);
        }
      } else {
        setThreads(regularThreads);
      }
      
      setLoading(false);
    } catch (err) {
      console.error('Error fetching threads:', err);
      setLoading(false);
    }
  };

  const fetchExecutives = async () => {
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant/executives`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setExecutives(data.executives || []);
      }
    } catch (err) {
      console.error('Error fetching executives:', err);
    }
  };

  const handleSelectThread = async (thread) => {
    setSelectedThread(thread);
    setShowNewMessage(false);
    
    // On mobile, hide thread list when opening a thread
    if (isMobile) {
      setShowThreadList(false);
    }
    
    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant-messages/threads/${thread.thread_id}`, {
        headers: getAuthHeaders()
      });
      
      if (res.ok) {
        const data = await res.json();
        setMessages(data.messages || []);
      }
    } catch (err) {
      console.error('Error fetching messages:', err);
    }
  };
  
  const handleBackToThreads = () => {
    setShowThreadList(true);
    setSelectedThread(null);
    setShowNewMessage(false);
  };

  const handleFileSelect = (e) => {
    console.log('handleFileSelect called', e.target.files);
    const files = Array.from(e.target.files);
    if (files.length === 0) {
      console.log('No files selected');
      return;
    }

    console.log(`Selected ${files.length} file(s):`, files.map(f => f.name));
    
    // Store files in state to display preview, upload will happen when sending
    const newPendingFiles = files.map((file, index) => {
      const id = `pending_${Date.now()}_${index}`;
      // Store the actual File object in ref (File objects don't survive React state well)
      pendingFilesRef.current[id] = file;
      
      return {
        id,
        file_name: file.name,
        file_type: file.type.startsWith('image/') ? 'image' : 
                   file.type.startsWith('video/') ? 'video' : 'document',
        mime_type: file.type,
        size: file.size,
        uploading: false,
        pending: true // Mark as pending upload
      };
    });

    setAttachments(prev => [...prev, ...newPendingFiles]);
    
    // Clear the input so same file can be selected again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  const handleRemoveAttachment = (attachmentId) => {
    // Clean up the file from the ref
    delete pendingFilesRef.current[attachmentId];
    setAttachments(prev => prev.filter(a => a.id !== attachmentId));
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim() && attachments.length === 0) return;

    // Upload any pending files first
    setUploading(true);
    const uploadedAttachments = [];
    
    for (const att of attachments) {
      if (att.pending) {
        // Get the actual File object from the ref
        const file = pendingFilesRef.current[att.id];
        if (!file) {
          console.error(`File not found in ref for ${att.file_name}`);
          alert(`Failed to upload ${att.file_name}. Please try again.`);
          setUploading(false);
          return;
        }
        
        // This is a pending file that needs to be uploaded
        try {
          const formData = new FormData();
          formData.append('file', file);
          
          // Mark as uploading
          setAttachments(prev => prev.map(a => 
            a.id === att.id ? { ...a, uploading: true } : a
          ));

          const res = await fetch(`${BACKEND_URL}/api/assistant-messages/upload`, {
            method: 'POST',
            
            body: formData
          });

          if (res.ok) {
            const data = await res.json();
            uploadedAttachments.push({
              id: data.attachment.id,
              file_name: data.attachment.file_name,
              mime_type: data.attachment.mime_type,
              file_type: data.attachment.file_type,
              size_bytes: data.attachment.size_bytes,
              storage_provider: data.attachment.storage_provider,
              storage_key: data.attachment.storage_key,
              file_url: data.attachment.file_url,
              uploaded_at: data.attachment.uploaded_at
            });
          } else {
            console.error(`Failed to upload ${att.file_name}`);
            alert(`Failed to upload ${att.file_name}. Please try again.`);
            setUploading(false);
            return;
          }
        } catch (err) {
          console.error('Upload error:', err);
          alert(`Failed to upload ${att.file_name}. Please try again.`);
          setUploading(false);
          return;
        }
      } else if (!att.pending) {
        // Already uploaded attachment
        uploadedAttachments.push({
          id: att.id,
          file_name: att.file_name,
          mime_type: att.mime_type,
          file_type: att.file_type,
          size_bytes: att.size || att.size_bytes || 0,
          storage_provider: att.storage_provider || "local",
          storage_key: att.storage_key || att.file_name,
          file_url: att.file_url,
          uploaded_at: att.uploaded_at || new Date().toISOString()
        });
      }
    }
    
    setUploading(false);

    const payload = {
      thread_id: selectedThread?.thread_id || null,
      recipient_id: selectedThread ? selectedThread.participant_id : selectedExecutive,
      subject: (!selectedThread && newSubject) ? newSubject : null,
      content: newMessage,
      attachments: uploadedAttachments
    };
    
    console.log('Sending payload:', JSON.stringify(payload, null, 2));

    try {
      const res = await fetch(`${BACKEND_URL}/api/assistant-messages`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        const data = await res.json();
        setNewMessage('');
        setNewSubject('');
        setAttachments([]);
        pendingFilesRef.current = {}; // Clear the files ref
        
        if (selectedThread) {
          // Refresh messages in current thread
          handleSelectThread(selectedThread);
        } else {
          // New thread created, refresh threads and select it
          await fetchThreads();
          const thread = threads.find(t => t.thread_id === data.thread_id);
          if (thread) handleSelectThread(thread);
          setShowNewMessage(false);
        }
        
        fetchThreads(); // Refresh thread list
      } else {
        const errorData = await res.json().catch(() => ({}));
        console.error('Send message failed:', errorData);
        alert(`Failed to send message: ${errorData.detail || 'Unknown error'}`);
      }
    } catch (err) {
      console.error('Error sending message:', err);
      alert('Failed to send message. Please try again.');
    }
  };

  const getFileIcon = (mimeType) => {
    if (!mimeType) return <File size={16} />;
    if (mimeType.startsWith('image/')) return <ImageIcon size={16} />;
    if (mimeType.startsWith('video/')) return <Video size={16} />;
    return <File size={16} />;
  };

  // formatFileSize is imported from fileHelpers

  if (loading) {
    return (
      <div style={{ minHeight: '100vh', background: '#F8F9FA', padding: '20px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <p style={{ color: '#6B7280' }}>Loading messages...</p>
      </div>
    );
  }

  return (
    <div style={{ minHeight: '100vh', background: '#F8F9FA', display: 'flex', flexDirection: 'column' }}>
      {/* Header */}
      <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: isMobile ? '12px 16px' : '16px 24px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <button
            onClick={() => {
              if ((isMobile && !showThreadList) || (!isMobile && (selectedThread || showNewMessage))) {
                handleBackToThreads();
              } else {
                navigate('/a-dashboard');
              }
            }}
            style={{
              padding: '8px',
              background: 'transparent',
              border: '1px solid #E5E7EB',
              borderRadius: '8px',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center'
            }}
            title={(!isMobile && (selectedThread || showNewMessage)) ? "Back to Thread List" : "Back to Dashboard"}
          >
            <ArrowLeft size={18} color="#6B7280" />
          </button>
          <div style={{ flex: 1 }}>
            <h1 style={{ color: '#0A0A0A', fontSize: isMobile ? '18px' : '20px', fontWeight: '600', margin: 0 }}>
              {isMobile && !showThreadList && selectedThread 
                ? selectedThread.participant_name 
                : 'Messages'}
            </h1>
            {(!isMobile || showThreadList) && (
              <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                Internal work inbox
              </p>
            )}
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Thread List - Hide on mobile when thread is selected */}
        {(!isMobile || showThreadList) && (
          <div style={{ 
            width: isMobile ? '100%' : '320px', 
            background: '#FFFFFF', 
            borderRight: isMobile ? 'none' : '1px solid #E5E7EB', 
            display: 'flex', 
            flexDirection: 'column' 
          }}>
          <div style={{ padding: '16px', borderBottom: '1px solid #E5E7EB' }}>
            <button
              onClick={() => {
                setSelectedThread(null);
                setMessages([]);
                setNewMessage('');
                setNewSubject('');
                setAttachments([]);
                setShowNewMessage(true);
                // On mobile, hide thread list when creating new message
                if (isMobile) {
                  setShowThreadList(false);
                }
              }}
              style={{
                width: '100%',
                padding: '10px',
                background: '#3B82F6',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: '8px',
                fontSize: '14px',
                fontWeight: '500',
                cursor: 'pointer'
              }}
            >
              + New Message
            </button>
          </div>

          <div style={{ flex: 1, overflowY: 'auto' }}>
            {threads.length === 0 ? (
              <div style={{ padding: '32px', textAlign: 'center' }}>
                <p style={{ color: '#9CA3AF', fontSize: '14px' }}>No messages yet</p>
              </div>
            ) : (
              threads.map(thread => (
                <div
                  key={thread.thread_id}
                  onClick={() => handleSelectThread(thread)}
                  style={{
                    padding: '16px',
                    borderBottom: '1px solid #F3F4F6',
                    cursor: 'pointer',
                    background: selectedThread?.thread_id === thread.thread_id ? '#F9FAFB' : '#FFFFFF',
                    transition: 'background 0.15s'
                  }}
                  onMouseEnter={(e) => {
                    if (selectedThread?.thread_id !== thread.thread_id) {
                      e.currentTarget.style.background = '#F9FAFB';
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (selectedThread?.thread_id !== thread.thread_id) {
                      e.currentTarget.style.background = '#FFFFFF';
                    }
                  }}
                >
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
                    <p style={{ color: '#0A0A0A', fontSize: '14px', fontWeight: thread.unread_count > 0 ? '600' : '500', margin: 0 }}>
                      {thread.participant_name}
                    </p>
                    {thread.unread_count > 0 && (
                      <span style={{
                        background: '#3B82F6',
                        color: '#FFFFFF',
                        fontSize: '11px',
                        padding: '2px 6px',
                        borderRadius: '10px',
                        fontWeight: '600'
                      }}>
                        {thread.unread_count}
                      </span>
                    )}
                  </div>
                  <p style={{ color: '#374151', fontSize: '13px', fontWeight: '500', margin: '0 0 4px 0' }}>
                    {thread.subject}
                  </p>
                  <p style={{ color: '#6B7280', fontSize: '12px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {thread.last_message}
                  </p>
                </div>
              ))
            )}
          </div>
        </div>
        )}

        {/* Message View - Hide on mobile when thread list is showing */}
        {(!isMobile || !showThreadList) && (
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', background: '#F9FAFB' }}>
            {!selectedThread && !showNewMessage ? (
              <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <div style={{ textAlign: 'center' }}>
                  <Send size={48} color="#E5E7EB" style={{ margin: '0 auto 16px' }} />
                  <p style={{ color: '#9CA3AF', fontSize: '14px' }}>Select a conversation or start a new message</p>
                </div>
              </div>
            ) : (
            <>
              {/* Message Header */}
              {selectedThread && (
                <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '16px 24px' }}>
                  <p style={{ color: '#0A0A0A', fontSize: '16px', fontWeight: '600', margin: '0 0 4px 0' }}>
                    {selectedThread.participant_name}
                  </p>
                  <p style={{ color: '#6B7280', fontSize: '13px', margin: 0 }}>
                    {selectedThread.subject}
                  </p>
                </div>
              )}

              {/* New Message Header */}
              {showNewMessage && (
                <div style={{ background: '#FFFFFF', borderBottom: '1px solid #E5E7EB', padding: '16px 24px' }}>
                  <div style={{ marginBottom: '12px' }}>
                    <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                      To:
                    </label>
                    <select
                      value={selectedExecutive}
                      onChange={(e) => setSelectedExecutive(e.target.value)}
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    >
                      <option value="">Select executive...</option>
                      {executives.map(exec => (
                        <option key={exec.id} value={exec.id}>{exec.full_name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label style={{ display: 'block', color: '#374151', fontSize: '13px', fontWeight: '500', marginBottom: '6px' }}>
                      Subject:
                    </label>
                    <input
                      type="text"
                      value={newSubject}
                      onChange={(e) => setNewSubject(e.target.value)}
                      placeholder="Message subject..."
                      style={{
                        width: '100%',
                        padding: '8px 12px',
                        border: '1px solid #D1D5DB',
                        borderRadius: '8px',
                        fontSize: '14px'
                      }}
                    />
                  </div>
                </div>
              )}

              {/* Messages */}
              <div style={{ 
                flex: showNewMessage && messages.length === 0 ? '0 0 auto' : 1, 
                overflowY: 'auto', 
                padding: isMobile ? '16px' : '24px' 
              }}>
                {messages.map((msg, index) => {
                  const isOwnMessage = msg.sender_type === 'assistant';
                  return (
                    <div
                      key={msg.id}
                      style={{
                        display: 'flex',
                        justifyContent: isOwnMessage ? 'flex-end' : 'flex-start',
                        marginBottom: isMobile ? '12px' : '16px'
                      }}
                    >
                      <div style={{
                        maxWidth: isMobile ? '80%' : '60%',
                        background: isOwnMessage ? '#3B82F6' : '#FFFFFF',
                        color: isOwnMessage ? '#FFFFFF' : '#0A0A0A',
                        padding: isMobile ? '10px 14px' : '12px 16px',
                        borderRadius: isMobile ? '16px' : '12px',
                        border: isOwnMessage ? 'none' : '1px solid #E5E7EB'
                      }}>
                        <p style={{ fontSize: '11px', color: isOwnMessage ? 'rgba(255,255,255,0.8)' : '#6B7280', marginBottom: '6px', fontWeight: '500' }}>
                          {msg.sender_name}
                        </p>
                        <p style={{ fontSize: '14px', margin: '0 0 8px 0', lineHeight: '1.5', whiteSpace: 'pre-wrap' }}>
                          {msg.content}
                        </p>
                        
                        {/* Attachments */}
                        {msg.attachments && msg.attachments.length > 0 && (
                          <div style={{ marginTop: '8px' }}>
                            {msg.attachments.map(att => (
                              <div
                                key={att.id}
                                style={{
                                  display: 'flex',
                                  alignItems: 'center',
                                  gap: '8px',
                                  padding: '8px',
                                  background: isOwnMessage ? 'rgba(255,255,255,0.2)' : '#F9FAFB',
                                  borderRadius: '6px',
                                  marginTop: '4px'
                                }}
                              >
                                <div style={{ color: isOwnMessage ? '#FFFFFF' : '#6B7280' }}>
                                  {getFileIcon(att.mime_type)}
                                </div>
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <p style={{ fontSize: '12px', margin: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                    {att.file_name}
                                  </p>
                                  <p style={{ fontSize: '11px', margin: 0, opacity: 0.7 }}>
                                    {formatFileSize(att.size_bytes)}
                                  </p>
                                </div>
                                <Download size={14} style={{ cursor: 'pointer' }} />
                              </div>
                            ))}
                          </div>
                        )}
                        
                        <p style={{ fontSize: '11px', color: isOwnMessage ? 'rgba(255,255,255,0.6)' : '#9CA3AF', marginTop: '6px', marginBottom: 0 }}>
                          {new Date(msg.created_at).toLocaleString('en-US', {
                            month: 'short',
                            day: 'numeric',
                            hour: 'numeric',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    </div>
                  );
                })}
                <div ref={messagesEndRef} />
              </div>

              {/* Message Input */}
              <div style={{ background: '#FFFFFF', borderTop: '1px solid #E5E7EB', padding: isMobile ? '12px 16px' : '16px 24px' }}>
                {/* Attachments Preview */}
                {attachments.length > 0 && (
                  <div style={{ marginBottom: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    {attachments.map(att => {
                      const progress = uploadProgress[att.id];
                      const isUploading = att.uploading && progress !== undefined;
                      
                      return (
                        <div
                          key={att.id}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            padding: '8px 12px',
                            background: isUploading ? '#EFF6FF' : '#F9FAFB',
                            border: `1px solid ${isUploading ? '#3B82F6' : '#E5E7EB'}`,
                            borderRadius: '8px',
                            position: 'relative',
                            overflow: 'hidden'
                          }}
                        >
                          {isUploading && (
                            <div style={{
                              position: 'absolute',
                              bottom: 0,
                              left: 0,
                              height: '2px',
                              width: `${progress}%`,
                              background: '#3B82F6',
                              transition: 'width 0.3s'
                            }} />
                          )}
                          
                          {isUploading ? (
                            <Upload size={14} color="#3B82F6" />
                          ) : (
                            getFileIcon(att.file_type || att.mime_type)
                          )}
                          
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '2px' }}>
                            <span style={{ fontSize: '13px', color: '#374151' }}>
                              {att.file_name}
                            </span>
                            {isUploading && (
                              <span style={{ fontSize: '11px', color: '#3B82F6' }}>
                                Uploading {progress}%
                              </span>
                            )}
                            {att.size && !isUploading && (
                              <span style={{ fontSize: '11px', color: '#6B7280' }}>
                                {formatFileSize(att.size)}
                              </span>
                            )}
                          </div>
                          
                          <button
                            onClick={() => handleRemoveAttachment(att.id)}
                            disabled={isUploading}
                            style={{
                              padding: '2px',
                              background: 'transparent',
                              border: 'none',
                              cursor: isUploading ? 'not-allowed' : 'pointer',
                              display: 'flex',
                              alignItems: 'center',
                              opacity: isUploading ? 0.5 : 1
                            }}
                          >
                            <X size={14} color="#6B7280" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end' }}>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileSelect}
                    multiple
                    accept="image/*,video/*,.pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,.txt,.csv"
                    style={{ display: 'none' }}
                  />
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      if (fileInputRef.current) {
                        fileInputRef.current.click();
                      } else {
                        console.error('File input ref is null');
                      }
                    }}
                    disabled={uploading}
                    type="button"
                    style={{
                      padding: '10px',
                      background: 'transparent',
                      border: '1px solid #E5E7EB',
                      borderRadius: '8px',
                      cursor: uploading ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      opacity: uploading ? 0.5 : 1
                    }}
                    title="Attach files"
                  >
                    <Paperclip size={18} color={uploading ? '#9CA3AF' : '#6B7280'} />
                  </button>
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder="Type your message..."
                    rows={1}
                    style={{
                      flex: 1,
                      padding: '10px 12px',
                      border: '1px solid #D1D5DB',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontFamily: 'inherit',
                      resize: 'none',
                      minHeight: '40px',
                      maxHeight: '120px'
                    }}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        handleSendMessage();
                      }
                    }}
                  />
                  <button
                    onClick={handleSendMessage}
                    disabled={!newMessage.trim() && attachments.length === 0}
                    style={{
                      padding: '10px 16px',
                      background: (!newMessage.trim() && attachments.length === 0) ? '#E5E7EB' : '#3B82F6',
                      color: '#FFFFFF',
                      border: 'none',
                      borderRadius: '8px',
                      fontSize: '14px',
                      fontWeight: '500',
                      cursor: (!newMessage.trim() && attachments.length === 0) ? 'not-allowed' : 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '6px'
                    }}
                  >
                    <Send size={16} />
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
          </div>
        )}
      </div>
    </div>
  );
};

export default MessagingPage;
